import {
  useDeleteTransaction,
  useGetORandSiQuery,
  useGetOrderSlipsQuery,
  usePostReprintOrSi,
  usePostReprintOS,
} from '@api/pos/pos-transaction-api.rtk';
import { getColor } from '@assets/theme';
import LoadingComponent from '@components/animation/loading-component';
import ControlledAutocomplete from '@components/form/controlled/controlled-autocomplete';
import CustomTable, {
  ICustomTableFunc,
  TableRowItem,
} from '@components/table/custom-table';
import DataTable from '@components/table/datatable';
import useDataTable from '@hooks/datatable/useDataTable';
import useAppContext from '@hooks/useAppContext';
import { ConfirmationDialog } from '@hooks/useConfirmationDialog';
import useCreateContext from '@hooks/useCreateContext';
import { UseDisclosureInterface } from '@hooks/useDisclosure';
import { ModalWrapper } from '@hooks/useModal';
import useToast from '@hooks/useToast';
import { PrinterDetails } from '@modules/point-of-sale/interface/bo-interface/printers.interface';
import {
  IPosReprint,
  IPosReprintOrSiPayload,
  IPosReprintOSPayload,
} from '@modules/point-of-sale/interface/orders/pos-orders.interface';
import {
  selectPOSPrinterMasterlist,
  selectPOSstationID,
  selectPOSTableTransactionID,
} from '@modules/point-of-sale/slice/pos-selector';
import { CancelOutlined, ClearOutlined, Search } from '@mui/icons-material';
import {
  LoadingButton,
  TabContext,
  TabList,
  TabPanel,
  useTabContext,
} from '@mui/lab';
import {
  Button,
  Checkbox,
  Chip,
  debounce,
  Dialog,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  SxProps,
  Tab,
  TextField,
  Typography,
} from '@mui/material';
import { GridColDef, GridRowId } from '@mui/x-data-grid';
import { GridApiCommunity } from '@mui/x-data-grid/internals';
import { useAppSelector } from '@store/index';
import { selectPropertyID } from '@store/selector/property.selector';
import { dateTimeFormat, formatNumber } from '@utils/format';
import { hashSignature } from '@utils/security';
import moment from 'moment';
import {
  ChangeEvent,
  Dispatch,
  MutableRefObject,
  RefObject,
  SetStateAction,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// #region CONTEXT
interface IPOSReprintCtx {
  filterByTransaction?: boolean;
  isVoid?: boolean;
  isAll?: boolean;
}
const { CtxProvider: ReprintCtxProvider, useCtx: useCategoryCtx } =
  useCreateContext<IPOSReprintCtx>('POSReprintCtx');
const usePOSReprintCtx = () => useCategoryCtx();

// #endregion

// #region CONFIG
type TPOSReprintTab = 'order-slip' | 'or-si' | 'charge-invoices';

const columnOrder = [
  'orderSlipID',
  'transactionID',
  'printerName',
  'osNo',
  'tableName',
  'punchedBy',
  'punchedDateAndTime',
];

const panelSX: SxProps = {
  p: 0,
  flex: 1,
  minHeight: { xs: 300, md: 450 },
  flexDirection: 'column',
};
// #endregion

// #region TABS
const POSChargeInvoices = () => {
  return <Stack sx={{ flex: 1, minHeight: 0 }}>POSChargeInvoices</Stack>;
};

const orSIColumns: GridColDef[] = [
  {
    field: 'seriesNo',
    headerName: 'Series No.',
    width: 110,
  },
  {
    field: 'stationName',
    headerName: 'Station Name',
    width: 150,
  },
  {
    field: 'cashier',
    headerName: 'Cashier',
    width: 150,
  },
  {
    field: 'amount',
    headerName: 'Amount',
    width: 120,
    headerAlign: 'right',
    align: 'right',
    valueFormatter: ({ value }) => formatNumber(value, 2, 2),
  },
  {
    field: 'salesDate',
    headerName: 'Sales Date',
    width: 110,
    valueFormatter: ({ value }) => {
      const date = moment(value);
      return date.format('MMM DD, YYYY');
    },
    valueGetter: (params) => new Date(params.value),
  },
  {
    field: 'settledDateAndTime',
    headerName: 'Settled Date and Time',
    width: 180,
    valueFormatter: ({ value }) => {
      const date = moment(value);
      return date.format(dateTimeFormat);
    },
    valueGetter: (params) => new Date(params.value),
  },
  {
    field: 'isVoid',
    headerName: 'Status',
    width: 120,
    headerAlign: 'center',
    align: 'center',
    sortable: false,
    renderCell: ({ value }) => {
      const status: string | boolean = value !== 'No';
      return (
        <Chip
          label={status ? 'Voided' : 'Posted'}
          sx={{
            bgcolor: status ? 'error.main' : 'success.main',
            color: '#fff',
          }}
          size='small'
        />
      );
    },
  },
];

const POSOrSi = ({
  isOpen,
  isCurrentDate,
  selectedRows,
  setSelectedRows,
  apiRef,
  voidStatus,
  getStatus,
  showAmount,
}: {
  isOpen: boolean;
  selectedRows: GridRowId[];
  setSelectedRows: Dispatch<SetStateAction<GridRowId[]>>;
  isCurrentDate: boolean;
  apiRef: MutableRefObject<GridApiCommunity>;
  voidStatus?: string;
  getStatus?: (data: string) => void;
  showAmount: boolean;
}) => {
  const { filterByTransaction, isVoid, isAll } = usePOSReprintCtx();
  const transactionID = useAppSelector(selectPOSTableTransactionID);

  const { value } = useTabContext() as { value: TPOSReprintTab };
  const { data, isLoading, isFetching } = useGetORandSiQuery(
    {
      ...(!isAll && { transactionID }),
      search: 'All',
      isVoided:
        voidStatus === 'No' || isVoid
          ? 'No'
          : voidStatus === 'Yes'
            ? 'Yes'
            : 'All',
    },
    {
      skip: !isOpen || value !== 'or-si',
    },
  );

  // FOR MULTIPLE SELECT
  // const handleSelectionChange = useCallback(
  //   (selectionModel: GridRowSelectionModel) => {
  //     setSelectedRows(selectionModel);
  //   },
  //   [],
  // );

  const orSIColumnsWithAmount = useMemo(() => {
    if (!showAmount) {
      return orSIColumns.filter((col) => col.field !== 'amount');
    }
    return orSIColumns;
  }, [showAmount]);

  return (
    <DataTable
      apiRef={apiRef}
      getRowId={(row) => row?.transactionID}
      columns={orSIColumnsWithAmount}
      checkboxSelection={false}
      rows={
        data
          ?.filter((item) => {
            const date = moment(item.settledDateAndTime);
            if (!date.isValid()) return false;

            if (filterByTransaction && transactionID !== item.transactionID) {
              return false;
            }

            return isCurrentDate ? date.isSame(moment(), 'day') : true;
          })
          .sort((a, b) => {
            const dateA = moment(a.settledDateAndTime);
            const dateB = moment(b.settledDateAndTime);
            return dateB.valueOf() - dateA.valueOf();
          })
          .filter(
            (item, index, self) =>
              index ===
              self.findIndex((t) => t.transactionID === item.transactionID),
          ) ?? []
      }
      disableRowSelectionOnClick
      tableStyle={{
        padding: 0,
        height: 'unset',
      }}
      wrapperStyle={{
        height: 'unset',
        display: 'flex',
        flex: 1,
        minHeight: 0,
      }}
      disableColumnFilter
      disableSearch
      disableColumnSelector
      loading={isLoading || isFetching}
      // onRowSelectionModelChange={handleSelectionChange}
      rowSelectionModel={selectedRows}
      onRowClick={(data) => {
        setSelectedRows((prev) => {
          const updated = prev.includes(data.id)
            ? prev.filter((i) => i !== data.id)
            : [data.id];
          return updated;
        });
        getStatus?.(data?.row?.isVoid);
      }}
    />
  );
};

const POSOrderSlips = ({
  tableRef,
  isCurrentDate,
  setSelectedRows,
  isOpen,
}: {
  tableRef: RefObject<ICustomTableFunc>;
  isCurrentDate: boolean;
  setSelectedRows: Dispatch<SetStateAction<GridRowId[]>>;
  isOpen: boolean;
}) => {
  const { filterByTransaction } = usePOSReprintCtx();
  const transactionID = useAppSelector(selectPOSTableTransactionID);

  const { value } = useTabContext() as { value: TPOSReprintTab };

  const { data, isLoading, isFetching } = useGetOrderSlipsQuery('', {
    skip: !isOpen || value !== 'order-slip',
    refetchOnMountOrArgChange: true,
  });
  const rows = useMemo(() => {
    if (!data) return [];

    return data
      .filter((item) => {
        const date = moment(item.punchedDateAndTime);
        if (!date.isValid()) return false;

        if (filterByTransaction && transactionID !== item.transactionID) {
          return false;
        }

        return isCurrentDate ? date.isSame(moment(), 'day') : true;
      })
      .sort((a, b) => {
        const dateA = moment(a.punchedDateAndTime);
        const dateB = moment(b.punchedDateAndTime);
        return dateB.valueOf() - dateA.valueOf();
      })
      .map((item) => {
        const row: Record<string, TableRowItem> = {};

        columnOrder.forEach((key) => {
          if (key in item) {
            row[key] = {
              value:
                key === 'punchedDateAndTime'
                  ? moment(item[key]).format(dateTimeFormat)
                  : item[key],
            };
          }
        });
        return row;
      });
  }, [data, columnOrder, dateTimeFormat, isCurrentDate, filterByTransaction]);

  const collapseRows = useMemo(() => {
    if (!data) return [];

    return data
      .filter((item) => {
        const date = moment(item.punchedDateAndTime);
        if (!date.isValid()) return false;

        if (isCurrentDate) {
          return date.isSame(moment(), 'day');
        }

        return true;
      })
      .sort((a, b) => {
        const dateA = moment(a.punchedDateAndTime);
        const dateB = moment(b.punchedDateAndTime);
        return dateB.valueOf() - dateA.valueOf();
      })
      .map((item) => {
        const itemsEntry = Object.entries(item).find(
          ([key]) => key === 'items',
        );
        if (!itemsEntry) return [];

        const [, value] = itemsEntry;
        const typedValue = value as IPosReprint['items'];
        if (!typedValue) return [];
        return typedValue.map((rawItem) =>
          Object.fromEntries(
            Object.entries(rawItem).map(([k, v]) => [
              k,
              {
                value: String(v),
                headerCellProps: { sx: { width: '11px' } },
                cellProps: { sx: { width: '100px' } },
              },
            ]),
          ),
        );
      });
  }, [data, isCurrentDate]);

  return (
    <CustomTable
      getRowId={(row) =>
        row?.orderSlipID?.value + '*' + row?.transactionID?.value
      }
      ref={tableRef}
      rows={rows}
      columns={{
        printerName: 'Printer Name',
        osNo: 'OS No',
        tableName: 'Table Name',
        punchedBy: 'Punched By',
        punchedDateAndTime: 'Punched Date and Time',
      }}
      collapseTitle='Items'
      collapseRows={collapseRows}
      loading={isLoading || isFetching}
      tableProps={{ size: 'small' }}
      checkboxSelection
      pagination
      hideColumns={['orderSlipID', 'transactionID']}
      onSelectionChange={(ids) => setSelectedRows(ids ?? [])}
      singleSelect
    />
  );
};
// #endregion

const POSReprintVoidModal = ({
  modalDisclosure,
  filterByTransaction,
  isVoid,
  isAll,
}: {
  modalDisclosure: UseDisclosureInterface;
  filterByTransaction?: boolean;
  isVoid?: boolean;
  isAll?: boolean;
}) => {
  // #region STATE
  const { discountBG, textSecondary } = getColor();
  const {
    openSecCodeDialog,
    //  startLoadingStatus,
    stopLoadingStatus,
  } = useAppContext();
  const { isOpen } = modalDisclosure;
  const { toast } = useToast();
  const tableRef = useRef<ICustomTableFunc>(null);
  const { apiRef, handleSearchValueChange } = useDataTable();
  const propertyID = useAppSelector(selectPropertyID);
  const stationID = useAppSelector(selectPOSstationID);
  const posPrinters = useAppSelector(selectPOSPrinterMasterlist);

  const [isCurrentDate, setIsCurrentDate] = useState(false);
  const [selectedRows, setSelectedRows] = useState<GridRowId[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [printer, setPrinter] = useState<PrinterDetails | null>(null);
  const [tabValue, setTabValue] = useState<TPOSReprintTab>(
    isVoid ? 'or-si' : 'order-slip',
  );
  const [selectedStatus, setSelectedStatus] = useState('');

  const handleTabChange = (_: SyntheticEvent, newValue: string) => {
    setSelectedRows([]);
    setTabValue(newValue as TPOSReprintTab);
  };
  const [isPrinting, setIsPrinting] = useState(false);

  useEffect(() => {
    if (!posPrinters || posPrinters.length === 0) return;
    setPrinter(posPrinters[0] ?? null);
  }, [posPrinters]);
  // #endregion

  // #region FUNCTION
  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        tableRef?.current?.handleSearch(value);
      }, 300),
    [tableRef],
  );

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (tabValue !== 'order-slip') {
      handleSearchValueChange(e, setSearchValue);
      return;
    }
    const value = e.target.value;
    setSearchValue(value);
    debouncedSearch(value);
  };

  const handleClear = () => {
    if (tabValue !== 'order-slip') {
      handleSearchValueChange('', setSearchValue);
      return;
    }
    setSearchValue('');
    debouncedSearch('');
  };

  const handleClose = () => {
    modalDisclosure.onClose();
    setTimeout(() => {
      setTabValue('order-slip');
      setSelectedRows([]);
      setSearchValue('');
      handleClear();
    }, 100);
  };
  // #endregion

  // #region REPRINT
  const [reprintOS, { isLoading: isReprintingOS }] = usePostReprintOS();
  const [reprintOrSi, { isLoading: isReprintingOrSi }] = usePostReprintOrSi();

  const handleReprint = async () => {
    await openSecCodeDialog(async (res) => {
      try {
        if (!res?.data.isValid) return;
        setIsPrinting(true);
        const selectedRow = selectedRows?.[0];

        if (!selectedRow) {
          toast({ message: 'No selected row found.' });
          return;
        }

        const printerID = posPrinters?.find(
          (item) => item.logicalDeviceName === printer?.logicalDeviceName,
        )?.printerID;

        if (!printerID) {
          toast({ message: 'No printer found.' });
          return;
        }

        if (tabValue === 'order-slip') {
          const payload: IPosReprintOSPayload = {
            properties: [
              {
                property: {
                  propertyID,
                  stationID,
                  transactionID: String(selectedRow).split('*')[1],
                  orderSlipID: String(selectedRow).split('*')[0],
                  printerID,
                  signature: hashSignature(
                    [
                      propertyID,
                      stationID,
                      String(selectedRow).split('*')[1],
                      String(selectedRow).split('*')[0],
                      printerID,
                    ].join(''),
                  ),
                },
              },
            ],
          };
          // startLoadingStatus('Reprinting Order Slip');

          const result = await reprintOS({
            payload,
          }).unwrap();

          if (result?.results?.responseCode === 200) {
            toast({
              severity: 'success',
              message: 'Order slip reprinted successfully',
            });
          } else {
            toast({
              severity: 'warning',
              message: 'Failed to print Order Slip',
            });
          }
        } else if (tabValue === 'or-si') {
          const status = selectedStatus !== 'No';
          const payload: IPosReprintOrSiPayload = {
            properties: [
              {
                property: {
                  propertyID,
                  stationID,
                  transactionID: String(selectedRow),
                  printerID,
                  isVoided: status,
                  signature: hashSignature(
                    [
                      propertyID,
                      stationID,
                      selectedRow,
                      printerID,
                      status ? 1 : 0,
                    ].join(''),
                  ),
                },
              },
            ],
          };
          // startLoadingStatus('Reprinting Invoice');

          const result = await reprintOrSi({
            transactionID: selectedRow,
            payload,
          }).unwrap();

          if (result?.results?.[0].responseCode === 200) {
            toast({
              severity: 'success',
              message: 'Invoice reprinted successfully',
            });
          } else {
            toast({
              message: 'Failed to print Invoice Report.',
              severity: 'error',
            });
          }
        }
      } catch (error) {
        toast({
          message: error,
          severity: 'warning',
        });
      } finally {
        setIsPrinting(false);
        stopLoadingStatus();
      }
    }, 'reprint_invoice');
  };
  // #endregion

  // #region VOID
  const [isOpenVoidDialog, setIsOpenVoidDialog] = useState(false);
  const [isOpenRemarks, setIsOpenRemarks] = useState(false);
  const [voidRemarks, setVoidRemarks] = useState('');
  const [approvedBy, setApprovedBy] = useState('');
  const [voidStatus, setVoidStatus] = useState('All');
  const [showAmount, setShowAmount] = useState(false);

  const debouncedChange = useCallback(
    debounce((newValue: string) => {
      setVoidRemarks?.(newValue);
    }, 20),
    [],
  );

  const [voidTransaction, { isLoading: isVoiding }] = useDeleteTransaction();

  // PROCESS 1
  const handleOpenVoidWarning = () => {
    setIsOpenVoidDialog(true);
  };

  const handleCloseVoidWarning = () => {
    setIsOpenVoidDialog(false);
    setIsOpenRemarks(false);
  };

  // PROCESS 2
  const handleOpenRemarks = async () => {
    setIsOpenVoidDialog(false);
    openSecCodeDialog(async (res) => {
      if (!res?.data.isValid) return;
      setIsOpenRemarks(true);
      setApprovedBy(res?.data?.user ?? 'No User Found');
    }, 'void_sales');
  };

  const handleCloseRemarks = () => {
    setVoidRemarks('');
    setIsOpenRemarks(false);
  };

  // PROCESS 3
  const handleOpenRemarksDialog = async () => {
    // setIsOpenRemarks(false);
    // setIsOpenRemarksDialog(true);
    await handleVoid();
  };

  // PROCESS 4
  // const handleWaste = () => {
  //   toast({ message: 'Items successfully wasted!', severity: 'success' });
  //   setVoidRemarks('');
  //   setIsOpenRemarksDialog(false);
  // };

  const handleVoid = async () => {
    try {
      const selectedRow = selectedRows?.[0];

      if (!selectedRow) {
        toast({ message: 'No selected row found.' });
        return;
      }

      const signature = hashSignature(
        [propertyID, voidRemarks, approvedBy]
          .map((v) => String(v ?? ''))
          .join(''),
      );

      const payload = {
        refId: selectedRow,
        payload: {
          properties: [
            {
              property: {
                propertyID,
                voidInvoices: [
                  {
                    voidInvoice: {
                      reason: voidRemarks,
                      approvedBy,
                      signature: signature,
                    },
                  },
                ],
                signature: hashSignature(`${propertyID}1`),
              },
            },
          ],
        },
      };
      // startLoadingStatus('Printing Void Invoice');

      const result = await voidTransaction(payload).unwrap();

      if (result?.results?.responseCode === 200) {
        toast({
          message: 'Transaction voided successfully',
          severity: 'success',
        });
      } else {
        toast({
          message: 'Failed to void Sales Invoice.',
          severity: 'error',
        });
      }
    } catch (error) {
      toast({
        message: error || 'Failed to void items. Please try again.',
        severity: 'error',
      });
    } finally {
      // Reset states
      setVoidRemarks('');
      setIsOpenRemarks(false);
      stopLoadingStatus();
    }
  };
  // #endregion

  const memoizedValue = useMemo(
    () => ({
      isVoid,
      filterByTransaction,
      isAll,
    }),
    [isVoid, filterByTransaction, isAll],
  );

  return (
    <>
      <ModalWrapper
        {...{
          modalDisclosure,
          withHeader: {
            title: isVoid ? 'Void' : `Reprint`,
            onClose:
              isReprintingOS || isReprintingOrSi ? () => {} : handleClose,
          },
        }}
      >
        <TabContext value={tabValue}>
          <ReprintCtxProvider value={memoizedValue}>
            <Stack sx={{ flex: 1, minHeight: 0 }}>
              {/* CONTENT */}
              <Stack
                sx={{
                  flex: 1,
                  minHeight: 0,
                  minWidth: { xs: '100%', md: '600px' },
                }}
              >
                {/* SEARCH */}
                <Stack
                  gap={2}
                  direction={{ xs: 'column-reverse', sm: 'row' }}
                  sx={{ justifyContent: 'space-between' }}
                >
                  <TabList
                    onChange={handleTabChange}
                    sx={{ '& .MuiButtonBase-root': { textTransform: 'none' } }}
                  >
                    {!isVoid && <Tab label='Order Slips' value='order-slip' />}
                    <Tab
                      label='Invoices'
                      value='or-si'
                      disabled={filterByTransaction}
                    />
                    {!isVoid && (
                      <Tab
                        label='Charge Invoices'
                        value='charge-invoices'
                        disabled
                      />
                    )}
                  </TabList>

                  <Stack flexDirection='row' gap={2} alignItems='center'>
                    {!isVoid && tabValue === 'or-si' && (
                      <FormControlLabel
                        label='Show Voided Only'
                        control={
                          <Checkbox
                            size='small'
                            checked={isVoid}
                            onChange={(e) =>
                              setVoidStatus(e.target.checked ? 'Yes' : 'All')
                            }
                          />
                        }
                      />
                    )}

                    <TextField
                      value={searchValue}
                      size='small'
                      placeholder='Search . . .'
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position='start'>
                            {searchValue ? (
                              <IconButton
                                size='small'
                                disableRipple
                                onClick={handleClear}
                              >
                                <ClearOutlined sx={{ fontSize: '16px' }} />
                              </IconButton>
                            ) : (
                              <Search sx={{ color: '#212121' }} />
                            )}
                          </InputAdornment>
                        ),
                      }}
                      onChange={handleSearchChange}
                    />
                  </Stack>
                </Stack>

                {/* DATATABLE */}
                <TabPanel
                  value='order-slip'
                  sx={{
                    ...panelSX,
                    display: tabValue === 'order-slip' ? 'flex' : 'none',
                  }}
                >
                  <POSOrderSlips
                    {...{
                      isCurrentDate,
                      tableRef,
                      setSelectedRows,
                      isOpen,
                    }}
                  />
                </TabPanel>

                <TabPanel
                  value='or-si'
                  sx={{
                    ...panelSX,
                    display: tabValue === 'or-si' ? 'flex' : 'none',
                  }}
                >
                  <POSOrSi
                    {...{
                      isOpen,
                      setSelectedRows,
                      isCurrentDate,
                      apiRef,
                      selectedRows,
                      voidStatus,
                      showAmount,
                      getStatus: (data) => setSelectedStatus(data),
                    }}
                  />
                </TabPanel>

                <TabPanel
                  value='charge-invoices'
                  sx={{
                    ...panelSX,
                    display: tabValue === 'charge-invoices' ? 'flex' : 'none',
                  }}
                >
                  <POSChargeInvoices />
                </TabPanel>
              </Stack>

              {/* BUTTON */}
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                gap={2}
                sx={{
                  position: 'sticky',
                  bottom: 0,
                  bgcolor: '#FFF',
                  justifyContent: 'space-between',
                  borderTop: '1px solid #EEE',
                  pt: 3,
                }}
              >
                <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size='small'
                        checked={isCurrentDate}
                        onChange={(_e, checked) => {
                          setSearchValue('');
                          setIsCurrentDate(checked);
                        }}
                      />
                    }
                    label='Current Date Only'
                  />

                  {tabValue === 'or-si' && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          size='small'
                          checked={showAmount}
                          onChange={(_e, checked) => {
                            if (!checked) {
                              setShowAmount(false);
                            } else {
                              openSecCodeDialog(async (res) => {
                                if (!res?.data.isValid) return;
                                setSearchValue('');
                                setShowAmount(checked);
                              }, 'void_sales');
                            }
                          }}
                        />
                      }
                      label='Show Amount'
                    />
                  )}
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} gap={1}>
                  {!isVoid && (
                    <ControlledAutocomplete
                      options={posPrinters}
                      getOptionLabel={(option) =>
                        `${option?.prefix} - ${option?.logicalDeviceName}`
                      }
                      selectedValue={printer}
                      setSelectedValue={setPrinter}
                      label='Reprint Where'
                      size='small'
                      sx={{
                        minWidth: 200,
                      }}
                      disableClearable
                    />
                  )}

                  <Button
                    variant='outlined'
                    onClick={handleClose}
                    disabled={isReprintingOS || isReprintingOrSi}
                  >
                    Close
                  </Button>
                  <LoadingButton
                    variant='contained'
                    disabled={selectedRows?.length <= 0}
                    onClick={isVoid ? handleOpenVoidWarning : handleReprint}
                    loading={isReprintingOS || isReprintingOrSi}
                  >
                    {isVoid ? 'Void' : 'Reprint'}
                  </LoadingButton>
                </Stack>
              </Stack>
            </Stack>
          </ReprintCtxProvider>
          {isPrinting && <LoadingComponent />}
        </TabContext>
      </ModalWrapper>

      {/* VOIDING DIALOG */}
      <>
        <ConfirmationDialog
          dialogStatus='warning'
          iconColor='warning'
          open={isOpenVoidDialog}
          title={`Void Item`}
          desc={`Are you sure you want to void the selected item(s)?`}
          onConfirm={handleOpenRemarks}
          onClose={handleCloseVoidWarning}
        />

        <Dialog open={isOpenRemarks}>
          <Stack sx={{ p: 3, gap: 1, width: '420px' }}>
            {/* HEADER */}
            <Stack alignItems='flex-start'>
              <Stack
                sx={{ bgcolor: discountBG, p: '12px', borderRadius: '100px' }}
              >
                <CancelOutlined
                  sx={{ color: 'primary.main', fontSize: '35px' }}
                />
              </Stack>
              <Typography variant='h5' sx={{ fontWeight: 600, py: 2 }}>
                Void Remarks
              </Typography>
            </Stack>
            {/* CONTENT */}
            <Stack gap={2} pb={3}>
              <Typography sx={{ color: textSecondary, fontSize: '16px' }}>
                Provide a reason for voiding the item.
              </Typography>
              <TextField
                multiline
                rows={5}
                label='Remarks'
                InputLabelProps={{
                  shrink: true,
                }}
                value={voidRemarks}
                onChange={(e) => debouncedChange(e.target.value)}
              />
            </Stack>
            {/* BUTTON */}
            <Stack direction='row' gap={1} ml='auto'>
              <Button
                variant='outlined'
                size='large'
                onClick={handleCloseRemarks}
                disabled={isVoiding}
              >
                Close
              </Button>
              <LoadingButton
                variant='contained'
                size='large'
                disabled={!voidRemarks}
                onClick={handleOpenRemarksDialog}
                loading={isVoiding}
              >
                Submit
              </LoadingButton>
            </Stack>
          </Stack>
        </Dialog>

        {/* <ConfirmationDialog
          dialogStatus='warning'
          iconColor='warning'
          open={isOpenRemarksDialog}
          title='Void Items'
          desc={
            <Stack gap={2} sx={{ maxWidth: '350px' }}>
              <Typography>
                The item(s) you voided contains one or more stock items. Select
                WASTE to deduct from the inventory otherwise click SAVE.
              </Typography>
              <Alert severity='error' sx={{ color: 'danger.main' }}>
                The action you're about to do cannot be undone. Should you need
                to offset the quantity, please do so using the Back Office.
              </Alert>
            </Stack>
          }
          onConfirm={handleVoid}
          confirmButtonTitle='Save'
          // backBtn={
          //   <Button variant='contained' color='error' onClick={handleWaste}>
          //     Waste
          //   </Button>
          // }
          backBtn={
            <Button
              variant='outlined'
              onClick={() => {
                setIsOpenRemarksDialog(false);
                setVoidRemarks('');
              }}
            >
              Cancel
            </Button>
          }
        /> */}
      </>
    </>
  );
};

export default POSReprintVoidModal;
